import React, { FC, useEffect, useState } from 'react';

import { CustomModal } from '@components';
import { translate } from '@utils/locale';
import { showGenericErrorToast } from '@utils/error';
import {
  TextField,
  Button,
  Card,
  CardMedia,
  CardContent,
  Typography
} from '@mui/material';

import { useValidateNewsMutation } from '../api';
import { NewsDetailsType, NewsVerificationModalProps } from './types';

export const NewsVerificationModal: FC<NewsVerificationModalProps> = ({
  colors,
  newsFields,
  updateNewsState,
  handleAppendNews,
  handleNewsSetValue,
  newsState
}) => {
  const [urlErrorMessage, setUrlErrorMessage] = useState<string>('');
  const [newsDetails, setNewsDetails] = useState<NewsDetailsType>(null);
  const [isSaveNewsButtonEnabled, setIsSaveNewsButtonEnabled] =
    useState<boolean>(false);
  const [isVerifyUrlButtonEnabled, setIsVerifyUrlButtonEnabled] =
    useState<boolean>(false);

  const [
    validateNews,
    { isLoading: newsValidationLoading, isError: newsValidationError }
  ] = useValidateNewsMutation();

  const handleSaveNews = () => {
    updateNewsState({ ...newsState, isNewsModalOpen: false });
    setUrlErrorMessage('');
    setIsVerifyUrlButtonEnabled(false);
    if (newsState.curNewsIndex === -1) {
      handleAppendNews(newsState.newsUrlValue);
    } else {
      handleNewsSetValue(newsState.curNewsIndex, newsState.newsUrlValue);
    }
    setNewsDetails(null);
  };

  const handleCancelNews = () => {
    updateNewsState({ ...newsState, isNewsModalOpen: false });
    setNewsDetails(null);
    setUrlErrorMessage('');
  };

  const handleUrlVerifyClick = async () => {
    try {
      const validationResponse = await validateNews({
        url: newsState.newsUrlValue
      }).unwrap();
      if (
        validationResponse.result.can_scrap === true &&
        validationResponse.result.details !== null
      ) {
        setIsSaveNewsButtonEnabled(true);
        setNewsDetails(validationResponse.result.details);
        setUrlErrorMessage('');
      } else {
        setIsSaveNewsButtonEnabled(false);
        setNewsDetails(null);
        setUrlErrorMessage(translate('modals.newsInvalidUrlError'));
      }
    } catch (err) {
      showGenericErrorToast(err);
      setUrlErrorMessage(translate('modals.newsInvalidUrlError'));
    }
  };

  const handleNewsUrlModalBody = () => {
    return (
      <div>
        <div className="flex items-center">
          <TextField
            label={translate('modals.newsInputField')}
            fullWidth={false}
            style={{
              width: '100%',
              marginTop: '0px',
              marginBottom: '0px'
            }}
            margin="normal"
            onChange={e =>
              updateNewsState({ ...newsState, newsUrlValue: e.target.value })
            }
            value={newsState.newsUrlValue}
          />
          <Button
            variant="contained"
            color="primary"
            style={{ marginLeft: '10px', height: '50px' }}
            onClick={handleUrlVerifyClick}
            disabled={!isVerifyUrlButtonEnabled}>
            {translate('modals.verifyButton')}
          </Button>
        </div>
        {urlErrorMessage && <p style={{ color: 'red' }}>{urlErrorMessage}</p>}
        {newsDetails && (
          <Card style={{ marginTop: '20px' }}>
            {newsDetails.image && (
              <CardMedia
                component="img"
                alt={newsDetails.title}
                height="140"
                image={newsDetails.image || undefined}
                title={newsDetails.title}
              />
            )}
            <CardContent>
              <Typography variant="h5" component="h2">
                {newsDetails.title}
              </Typography>
              <Typography color="textSecondary">
                {newsDetails.description}
              </Typography>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  useEffect(() => {
    setIsVerifyUrlButtonEnabled(
      newsState.newsUrlValue.trim() !== '' &&
        newsState.newsUrlValue !== newsFields[newsState.curNewsIndex]?.url
    );
    setIsSaveNewsButtonEnabled(false);
  }, [newsState.newsUrlValue]);

  useEffect(() => {
    if (newsValidationError) {
      setNewsDetails(null);
    }
  }, [newsValidationError]);

  return (
    <CustomModal
      width={'40%'}
      buttongroupStyles="pt-4"
      isModalOpen={newsState.isNewsModalOpen}
      secondaryButtonVariant="text"
      isPrimaryButtonLoading={newsValidationLoading}
      primaryButtonLabel={translate('modals.saveVerifiedUrlButton')}
      secondaryButtonLabel={translate('modals.cancelButtonTitle')}
      onChangePrimaryButton={handleSaveNews}
      onChangeSecondaryButton={handleCancelNews}
      header={translate('modals.verifyNewsUrlModal')}
      body={handleNewsUrlModalBody()}
      primaryButtonCustomStyle={{
        backgroundColor: colors.primaryBlue
      }}
      secondaryButtonCustomStyle={{
        color: colors.primaryBlue,
        opacity: 0.8
      }}
      isPrimaryButtonDisabled={!isSaveNewsButtonEnabled}
    />
  );
};
